import { FlatterFilesResponse } from "./fitting-product.model";
import { LookupValue } from "./lookup.model";

export interface ClosureProduct {
  id?: number;
  partNumber?: string;
  productName?: string;
  description?: string | null;
  diameterId?: number;
  flanged?: boolean | number;
  wallThicknessId?: number;
  pressureRatingId?: number;
  maxDesignPressure?: number;
  minDesignTemp?: number | null;
  maxDesignTemp?: number | null;
  hingeOrientationId?: number;
  clampRingId?: number;
  designFactorId?: number;
  flangeClassId?: number | null;
  classRatingId?: number | null;
  weight?: string | null;
  restricted?: boolean | number;
  restrictedNotice?: string | null;
  inactiveDate?: Date | null;
  drawing?: string;
  notes?: string | null;
  createdDate?: Date;
  createdBy?: string;
  updatedDate?: Date;
  updatedBy?: string;

  diameter?: LookupValue;
  wallThickness?: LookupValue;
  pressureRating?: LookupValue;
  hingeOrientation?: LookupValue;
  clampRing?: LookupValue;
  designFactor?: LookupValue;
  flangeClass?: LookupValue;

  closureProductAssociatedProducts?: ClosureProductAssociatedProduct[];
  closureProductDesignCodes?: ClosureProductDesignCode[];
  closureProductDesignPressures?: ClosureProductDesignPressure[];

  flatterFilesExistence?: { [key: string]: FlatterFilesResponse }
}

export function closureProductFactory(obj: Partial<ClosureProduct>): ClosureProduct {
  return {
    id: obj.id,
    partNumber: obj.partNumber,
    productName: obj.productName,
    description: obj.description,
    diameterId: obj.diameterId,
    flanged: obj.flanged,
    wallThicknessId: obj.wallThicknessId,
    pressureRatingId: obj.pressureRatingId,
    maxDesignPressure: obj.maxDesignPressure,
    minDesignTemp: obj.minDesignTemp,
    maxDesignTemp: obj.maxDesignTemp,
    hingeOrientationId: obj.hingeOrientationId,
    clampRingId: obj.clampRingId,
    designFactorId: obj.designFactorId,
    flangeClassId: obj.flangeClassId,
    classRatingId: obj.classRatingId,
    weight: obj.weight,
    restricted: obj.restricted,
    restrictedNotice: obj.restrictedNotice,
    inactiveDate: obj.inactiveDate,
    drawing: obj.drawing,
    notes: obj.notes,
    createdDate: obj.createdDate,
    createdBy: obj.createdBy,
    updatedDate: obj.updatedDate,
    updatedBy: obj.updatedBy,
    diameter: obj.diameter,
    wallThickness: obj.wallThickness,
    pressureRating: obj.pressureRating,
    hingeOrientation: obj.hingeOrientation,
    clampRing: obj.clampRing,
    designFactor: obj.designFactor,

    closureProductDesignCodes: obj.closureProductDesignCodes,
    closureProductAssociatedProducts: obj.closureProductAssociatedProducts,
    closureProductDesignPressures: obj.closureProductDesignPressures,
    flatterFilesExistence: obj.flatterFilesExistence
  }
}

export interface ClosureAdminFilter {
  partNumber?: string;
  partName?: string;
  description?: string;
  diameterIds?: number[];
  flanged?: boolean;
  wallThicknessIds?: number[];
  hingeOrientationIds?: number[];
  clampRingId?: number[];
  designFactorIds?: number[];
  toOdataFilter(): string;
}


export function closuresAdminFilterFactory(obj: Partial<ClosureAdminFilter>): ClosureAdminFilter {
  return {
    partNumber: obj.partNumber,
    partName: obj.partName,
    description: obj.description,
    diameterIds: obj.diameterIds,
    flanged: obj.flanged,
    wallThicknessIds: obj.wallThicknessIds,
    hingeOrientationIds: obj.hingeOrientationIds,
    clampRingId: obj.clampRingId,
    designFactorIds: obj.designFactorIds,
    toOdataFilter: () => {
      let str = '';

      if ((obj.partNumber || '').length > 0) str += `contains(PartNumber, '${obj.partNumber}') AND `;
      if ((obj.partName || '').length > 0) str += `contains(ProductName, '${obj.partName}') AND `;
      if ((obj.description || '').length > 0) str += `contains(Description, '${obj.description}') AND `;
      if (!!obj.flanged) str += `flanged eq 1 AND `;
      if ((obj.diameterIds || []).length > 0) str += `DiameterId in (${obj.diameterIds?.map(v => `${v},`).map(v => v.substring(0, v.length - 1))}) AND `;
      if ((obj.wallThicknessIds || []).length > 0) str += `WallThicknessId in (${obj.wallThicknessIds?.map(v => `${v},`).map(v => v.substring(0, v.length - 1))}) AND `;
      if ((obj.hingeOrientationIds || []).length > 0) str += `HingeOrientationId in (${obj.hingeOrientationIds?.map(v => `${v},`).map(v => v.substring(0, v.length - 1))}) AND `;
      if ((obj.clampRingId || []).length > 0) str += `ClampRingId in (${obj.clampRingId?.map(v => `${v},`).map(v => v.substring(0, v.length - 1))}) AND `;
      if ((obj.designFactorIds || []).length > 0) str += `DesignFactorId in (${obj.designFactorIds?.map(v => `${v},`).map(v => v.substring(0, v.length - 1))}) AND `;
      return str.substring(0, str.length - 5);
    }
  }
}

export interface ClosureProductAssociatedProduct {
  id: number;
  closureProductId: number;
  associatedProductId: number;
  notes: null;
  sortOrder: number;
  includedInSystemKit: boolean;
  displayColor: string;
  displayBold: boolean;
  createdDate: string;
  createdBy: string;
  updatedDate: string;
  updatedBy: string;
}

export interface ClosureProductDesignPressure {
  id: number;
  designFactorId: number;
  closureProductId: number;
  maxDesignPressure: number;
  createdDate: string;
  createdBy: string;
  updatedDate: string;
  updatedBy: string;

  closureDesignFactor: LookupValue;
}

export interface ClosureProductDesignCode {
  id: number;
  designCodeId: number;
  fittingProductId: number;
  createdDate: string;
  createdBy: string;
  updatedDate: string;
  updatedBy: string;

  designCode: LookupValue;
}
